import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";

import { catchError, concatMap, map, switchMap, take } from "rxjs/operators";
import { of } from "rxjs";
import { AppState, PatientApiService } from "features";
import { select, Store } from "@ngrx/store";
import {
	GetMobilePatientById,
	GetMobilePatientByIdError,
	GetMobilePatientByIdSuccess,
	GetPatientById,
	GetPatientByIdError,
	GetPatientByIdSuccess,
	ModificationDocumentAttestationPatient,
	ModificationDocumentAttestationPatientError,
	ModificationDocumentAttestationPatientSuccess,
	ModificationMutuelleAttestationPatient,
	ModificationMutuelleAttestationPatientError,
	ModificationMutuelleAttestationPatientSuccess,
	ModifMutuellePatientActionTypes,
} from "@featuresAY/mutuelle-web-ay/store/modif-mutuelle-patient.action";
import { ModificationMutuelleAttestationPatientApiService } from "@featuresAY/mutuelle-web-ay/services/api/modification-mutuelle-attestation-patient-api.service";
import { DocumentPatient, MutuelleAttestationPatient } from "@jack-sparrow-libraries/emotion-models";
import {
	selectModifDocumentMutuelleAttestation,
	selectModifMutuelle,
} from "@featuresAY/mutuelle-web-ay/store/modif-mutuelle-patient.selector";

@Injectable()
export class ModifMutuellePatientEffect {
	constructor(
		private actions$: Actions,
		private store: Store<AppState>,
		private patientApi: PatientApiService,
		private modificationMutuelleApi: ModificationMutuelleAttestationPatientApiService,
	) {}

	patientById$ = createEffect(() => {
		return this.actions$.pipe(
			ofType<GetPatientById>(ModifMutuellePatientActionTypes.GetPatientById),
			concatMap((action) => {
				return this.patientApi.loadPatient(action.payload).pipe(
					map((patient) => {
						return new GetPatientByIdSuccess(patient);
					}),
					catchError((err) => {
						return of(new GetPatientByIdError(err));
					}),
				);
			}),
			catchError((error) => {
				return of(new GetPatientByIdError(error));
			}),
		);
	});

	mobilePatientById$ = createEffect(() => {
		return this.actions$.pipe(
			ofType<GetMobilePatientById>(ModifMutuellePatientActionTypes.GetMobilePatientById),
			concatMap((action) => {
				return this.modificationMutuelleApi.loadMobilePatient(action.payload).pipe(
					map((patient) => {
						return new GetMobilePatientByIdSuccess(patient);
					}),
					catchError((err) => {
						return of(new GetMobilePatientByIdError(err));
					}),
				);
			}),
			catchError((error) => {
				return of(new GetMobilePatientByIdError(error));
			}),
		);
	});

	updateDocumentMutuellePatient$ = createEffect(() => {
		return this.actions$.pipe(
			ofType<ModificationDocumentAttestationPatient>(ModifMutuellePatientActionTypes.ModifDocAttestationPatient),
			switchMap((action) => {
				return this.store.pipe(
					select(selectModifDocumentMutuelleAttestation),
					take(1),
					switchMap((documentAttestation: DocumentPatient) => {
						if (documentAttestation) {
							// si un document a été modifié
							return this.modificationMutuelleApi
								.updateDocumentMutuelleAttestation(documentAttestation, action.payload.patient.id)
								.pipe(
									switchMap((doc) => {
										return [
											new ModificationDocumentAttestationPatientSuccess(action.payload.patient),
										];
									}),
								);
						}
						// si aucun doc n'est mis, je modifie directement la mutuelle attestatio
						return [new ModificationMutuelleAttestationPatient(action.payload.mutuelle)];
					}),
					catchError((error) => {
						return of(new ModificationDocumentAttestationPatientError(error));
					}),
				);
			}),
			catchError((error) => {
				return of(new ModificationDocumentAttestationPatientError(error));
			}),
		);
	});

	updateDocumentMutuellePatientsuccess$ = createEffect(() => {
		return this.actions$.pipe(
			ofType<ModificationDocumentAttestationPatientSuccess>(
				ModifMutuellePatientActionTypes.ModifDocAttestationPatientSuccess,
			),
			switchMap((action) => {
				return this.store.pipe(
					select(selectModifMutuelle),
					take(1),
					switchMap((mutuelle: MutuelleAttestationPatient) => {
						return [new ModificationMutuelleAttestationPatient(mutuelle)];
					}),
					catchError((error) => {
						return of(new ModificationDocumentAttestationPatientError(error));
					}),
				);
			}),
			catchError((error) => {
				return of(new ModificationDocumentAttestationPatientError(error));
			}),
		);
	});

	updateMutuellePatient$ = createEffect(() => {
		return this.actions$.pipe(
			ofType<ModificationMutuelleAttestationPatient>(
				ModifMutuellePatientActionTypes.ModifMutuelleAttestationPatient,
			),
			switchMap((action) => {
				return this.modificationMutuelleApi.updateMutuelleAttestationPatient(action.payload).pipe(
					switchMap((mutuelle) => {
						return [new ModificationMutuelleAttestationPatientSuccess(mutuelle)];
					}),
					catchError((error) => {
						return of(new ModificationMutuelleAttestationPatientError(error));
					}),
				);
			}),
			catchError((error) => {
				return of(new ModificationMutuelleAttestationPatientError(error));
			}),
		);
	});
}
